import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  output,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TuiIcon, TuiButton } from '@taiga-ui/core';
import { RequestAddressesService } from '@core/services/request/request-addresses.service';
import { RequestService } from '@core/services/request/request.service';
import { OutkeepPermissionService } from '@core/services/permission/outkeep-permission.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { GroupActionsViews } from '@features/requests/request-list/types';
import { TranslatePipe } from '@ngx-translate/core';
import { switchMap } from 'rxjs';
import { OutkeepPermission } from '@outkeep/shared';

@Component({
  selector: 'x-main-group-actions-view',
  imports: [CommonModule, TuiButton, TuiIcon, TranslatePipe],
  templateUrl: './main-group-actions-view.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MainGroupActionsViewComponent {
  $viewChanged = output<GroupActionsViews>({
    alias: 'viewChangedOutput',
  });
  private readonly destroyRef = inject(DestroyRef);
  private readonly requestAddressesService = inject(RequestAddressesService);
  public readonly GroupActionsViewsTypes = GroupActionsViews;
  public readonly requestService = inject(RequestService);
  public readonly permissionService = inject(OutkeepPermissionService);
  public readonly canLogistChangeInstaller$ =
    this.permissionService.canLogistChangeInstaller$;
  public readonly canUploadFiles$ = this.permissionService.hasPermission$([
    OutkeepPermission.LogistFilesUpload,
    OutkeepPermission.LogistOnlyOwnerFilesUpload,
    OutkeepPermission.LogistOnlyContractorFilesUpload,
  ]);
  public readonly canLogistViewAllRequests$ =
    this.permissionService.canLogistViewAllRequests$;

  $selectedRequests = this.requestService.selectedRequests;

  public performResettingActualDeliveryAddress() {
    this.requestAddressesService
      .resetActualDeliveryAddress$(this.$selectedRequests())
      .pipe(
        switchMap(() => {
          return this.requestService.reloadSelectedRequests$();
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  public canOpenInstallerAssignmentAction(): boolean {
    return this.installerAssignmentBlockedReasonKey() === null;
  }

  public installerAssignmentBlockedReasonKey(): string | null {
    const selectedRequests = this.$selectedRequests();

    if (!selectedRequests.length) {
      return 'group_actions.installer_assignment.no_selected_requests';
    }

    const firstOwnerId = selectedRequests[0].counterpartyOwnerId.id;

    return selectedRequests.every(
      request => request.counterpartyOwnerId.id === firstOwnerId,
    )
      ? null
      : 'group_actions.installer_assignment.mixed_owners';
  }
}
